import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ApperIcon from '@/components/ApperIcon';

const FIELDS = ['Name', 'service_c', 'customerName_c', 'status_c', 'scheduledAt_c', 'address_c', 'phone_c'];

export const route = { path: '/schedule', layout: 'owner', access: "profile === 'Worker_c'" };
export const nav = { icon: 'CalendarDays', label: 'My schedule', section: 'Work', profiles: ['Worker_c'], order: 20 };

export default function Schedule() {
  const [day, setDay] = useState('today');
  const [search, setSearch] = useState('');
  const { data, loading, error, run } = useFetch(() => {
    const { contains } = window.ApperSDK.CoreSDK;
    let query = sdk.table('jobs_c').select(FIELDS);
    if (search.trim()) query = query.where(contains('Name', search.trim()));
    return query.orderBy('scheduledAt_c').fetch().then((result) => {
      if (!result.success) throw new Error(result.message || 'Could not load schedule');
      return result.data;
    });
  }, [search]);
  const jobs = data ?? [];
  const today = new Date();
  const todayKey = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = tomorrow.getFullYear() + '-' + String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + String(tomorrow.getDate()).padStart(2, '0');
  const visibleJobs = useMemo(() => jobs.filter((job) => {
    const date = job.scheduledAt_c ? new Date(job.scheduledAt_c) : null;
    const dateKey = date ? date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0') : '';
    if (day === 'today') return dateKey === todayKey;
    if (day === 'tomorrow') return dateKey === tomorrowKey;
    return true;
  }), [jobs, day, todayKey, tomorrowKey]);
  const grouped = useMemo(() => visibleJobs.reduce((groups, job) => {
    const key = job.scheduledAt_c ? new Date(job.scheduledAt_c).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' }) : 'Unscheduled';
    groups[key] = [...(groups[key] ?? []), job];
    return groups;
  }, {}), [visibleJobs]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Field planning</p>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">My schedule</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">A time-first view of service visits, so you can plan travel and prepare tools before each appointment.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={day === 'today' ? 'default' : 'outline'} onClick={() => setDay('today')}>Today</Button>
          <Button variant={day === 'tomorrow' ? 'default' : 'outline'} onClick={() => setDay('tomorrow')}>Tomorrow</Button>
          <Button variant={day === 'all' ? 'default' : 'outline'} onClick={() => setDay('all')}>All</Button>
        </div>
      </header>
      <section className="rounded-3xl border border-white/50 bg-card/75 p-4 shadow-md backdrop-blur-xl sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <ApperIcon name="Search" size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search job or service…" aria-label="Search schedule" />
          </div>
          <Button variant="outline" onClick={run} disabled={loading}><ApperIcon name="RefreshCw" size={15} className="mr-2" />Refresh</Button>
        </div>
        <div className="mt-5 flex items-center justify-between border-b border-border/70 pb-3">
          <div><h2 className="font-heading text-lg font-bold">{day === 'today' ? 'Today’s visits' : day === 'tomorrow' ? 'Tomorrow’s visits' : 'All scheduled visits'}</h2><p className="mt-1 text-xs text-muted-foreground">{visibleJobs.length} visit{visibleJobs.length === 1 ? '' : 's'} in this view</p></div>
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">Local time</span>
        </div>
        {loading ? <div className="space-y-3 py-5">{[1, 2, 3].map((item) => <div key={item} className="h-24 animate-pulse rounded-2xl bg-muted/70" />)}</div> :
          error ? <div className="py-10 text-center"><p className="font-semibold text-destructive">Schedule could not load</p><p className="mt-1 text-sm text-muted-foreground">{error.message}</p><Button className="mt-4" variant="outline" onClick={run}>Try again</Button></div> :
          visibleJobs.length ? <div className="space-y-6 py-5">
            {Object.entries(grouped).map(([date, rows]) => <section key={date}>
              <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{date}</h3>
              <div className="space-y-3">
                {rows.map((job) => <Link key={job.Id} to={'/jobs/' + job.Id} className="group grid gap-4 rounded-2xl border border-border/80 bg-background/75 p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm sm:grid-cols-[100px_minmax(0,1fr)_auto] sm:items-center">
                  <div className="border-l-2 border-primary pl-3"><p className="font-heading text-lg font-bold">{formatTime(job.scheduledAt_c)}</p><p className="mt-1 text-xs text-muted-foreground">{job.status_c}</p></div>
                  <div className="min-w-0"><p className="truncate font-semibold">{job.Name}</p><p className="mt-1 text-sm text-muted-foreground">{job.customerName_c || 'Customer details pending'} · {job.service_c || 'General service'}</p><p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground"><ApperIcon name="MapPin" size={13} className="mt-0.5 shrink-0" />{job.address_c || 'Address not available'}</p></div>
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-primary">Open job <ApperIcon name="ArrowUpRight" size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                </Link>)}
              </div>
            </section>)}
          </div> :
          <div className="grid place-items-center py-14 text-center"><div className="grid h-12 w-12 place-items-center rounded-2xl bg-muted text-muted-foreground"><ApperIcon name="CalendarDays" size={22} /></div><h3 className="mt-4 font-heading text-lg font-bold">No visits in this view</h3><p className="mt-1 max-w-sm text-sm text-muted-foreground">Try another date or clear your search. New assignments will appear here when connected to the live job system.</p><Button className="mt-4" variant="outline" onClick={() => { setDay('all'); setSearch(''); }}>Show all visits</Button></div>}
      </section>
      <p className="text-xs leading-5 text-muted-foreground">Current build note: this portal is using demo records. Date filtering and navigation work, but the list is not yet restricted by a live worker assignment relationship.</p>
    </div>
  );
}

function formatTime(value) {
  if (!value) return 'No time';
  return new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}
